import React from 'react';
import PropTypes from 'prop-types';
import SelectDropdown from '../../../components/SelectDropdown';
import DatePicker from '../../../components/DatePicker';
import { getFilteredStoreIds } from '../../../constants/utils';
import { RegionAndBanchDownloadButton } from '../../../components/DownloadReport/RegionBranchDownloadReport';

const FilterControls = ({
  regionOptions,
  regionId,
  resetRegion,
  handleResetForRegion,
  handleRegionIdChange,
  options,
  regionBranches,
  userData,
  storeId,
  resetStore,
  handleResetForStore,
  handleStoreIdChange,
  defaultFilter,
  componentType,
  isDisabled,
  branchDownloadFilters,
  localSpendFilters,
  spendToggleState,
  regionDownloadFilters,
  subBranchDashboard,
}) => {
  const getFilteredOptions = () => {
    // If there are region-specific branches already determined, use those
    if (regionBranches?.length) {
      return regionBranches;
    }
    // If user is admin and regionId is empty string (None selected), show all branches
    else if (userData?.data?.is_gale_admin && regionId === '') {
      return options;
    }
    // If user is admin but a specific region is selected or not selected yet
    else if (userData?.data?.is_gale_admin) {
      return options;
    }
    // For non-admin users, filter by first region in the list
    else {
      return options?.filter((store) => String(store.region) === String(regionOptions[0]?.value));
    }
  };

  const getDefaultBranchValues = () => {
    if (regionBranches?.length) {
      return [...regionBranches.map((branch) => branch?.value)];
    } else if (storeId?.length) {
      return getFilteredStoreIds(storeId, options);
    } else {
      return [1];
    }
  };

  return (
    <div
      style={{
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '13px',
        width: '90%',
        margin: 'auto',
        marginTop: '20px',
      }}
    >
      <div style={{ marginRight: !subBranchDashboard && '20px' }}>
        {regionOptions?.length !== 0 && !subBranchDashboard ? (
          <SelectDropdown
            options={regionOptions}
            label={'Select Region'}
            resetValues={resetRegion}
            handleResetValues={handleResetForRegion}
            singleSelectDefaultValue={regionId || regionOptions?.[0]?.value}
            width={'200px'}
            handleFilterChange={handleRegionIdChange}
            marginExist={false}
            disabled={isDisabled}
          />
        ) : null}
      </div>

      <div style={{ marginRight: 'auto', marginLeft: subBranchDashboard && '-12px' }}>
        <SelectDropdown
          options={getFilteredOptions()}
          label={'Select Branch'}
          isSelectAll={true}
          multiple={true}
          multiSelectDefaultValues={getDefaultBranchValues()}
          updateMultipleDropdownValue={getFilteredStoreIds(storeId, options)}
          allOptionsSelectedText={'All Branches'}
          resetValues={resetStore}
          handleResetValues={handleResetForStore}
          width={'200px'}
          handleFilterChange={handleStoreIdChange}
          marginExist={false}
          excludeGaleMarketSelWithOtherBranch
          regionalBranches={Boolean(regionBranches?.length) || subBranchDashboard}
          disabled={isDisabled}
          regionIdDependencyValue={regionId}
        />
      </div>

      <DatePicker setFilter={defaultFilter} component={componentType} combineCompare={false} />

      <RegionAndBanchDownloadButton
        disabled={isDisabled || (subBranchDashboard && !storeId?.length)}
        branchDownloadFilters={branchDownloadFilters}
        regionDownloadFilters={regionDownloadFilters}
        localSpendFilters={localSpendFilters}
        spendToggleState={spendToggleState}
        showRegionButton={!subBranchDashboard}
        subBranchDashboard={subBranchDashboard}
      />
    </div>
  );
};

export default React.memo(FilterControls);

FilterControls.propTypes = {
  // Region selection props
  regionOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ),
  regionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  resetRegion: PropTypes.bool,
  handleResetForRegion: PropTypes.func,
  handleRegionIdChange: PropTypes.func,

  // Branch/Store selection props
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      region: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  regionBranches: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      region: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  userData: PropTypes.shape({
    data: PropTypes.shape({
      is_gale_admin: PropTypes.bool,
    }),
  }),
  storeId: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  resetStore: PropTypes.bool,
  handleResetForStore: PropTypes.func,
  handleStoreIdChange: PropTypes.func,

  // Filter and component type props
  defaultFilter: PropTypes.func,
  componentType: PropTypes.string,
  isDisabled: PropTypes.bool,

  // Download button props
  branchDownloadFilters: PropTypes.object,
  localSpendFilters: PropTypes.object,
  spendToggleState: PropTypes.bool,
  regionDownloadFilters: PropTypes.object,
  subBranchDashboard: PropTypes.bool,
};
